import React from "react"

import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { Theme } from "@mui/material/styles"
import { useTranslation } from "react-i18next"

import useSession from "@hooks/useSession"

import { SubMenuItemWithChildren } from "../SubMenuItemWithChildren"
import { SubMenuItemWithClickHandler } from "../SubMenuItemWithClickHandler"
import { SubMenuItemWithLink } from "../SubMenuItemWithLink"

export interface SubMenu {
  id: string
  translatedTextKey?: string
  items: (
    | SubMenuItemWithChildren
    | SubMenuItemWithLink
    | SubMenuItemWithClickHandler
  )[]
}

export const SubMenu: React.FC<Omit<SubMenu, "id">> = ({
  translatedTextKey,
  items,
}) => {
  const { t } = useTranslation()
  const { hasPermission } = useSession()

  const filteredItems = items.filter((item) => {
    if (item.permission && !hasPermission(item.permission)) {
      return false
    }
    return true
  })

  if (filteredItems.length === 0) {
    return null
  }

  return (
    <Stack direction="column" gap={0.5}>
      {translatedTextKey && (
        <Typography
          variant="overline"
          sx={(theme: Theme) => ({
            paddingLeft: theme.spacing(1),
            color: theme.palette.text.secondary,
          })}
        >
          {t(translatedTextKey)}
        </Typography>
      )}

      {filteredItems.map((item) => {
        if ("items" in item) {
          return (
            <SubMenuItemWithChildren
              key={item.translatedTextKey}
              icon={item.icon}
              translatedTextKey={item.translatedTextKey}
              items={item.items}
            />
          )
        }

        if ("route" in item) {
          return (
            <SubMenuItemWithLink
              key={item.translatedTextKey}
              icon={item.icon}
              translatedTextKey={item.translatedTextKey}
              route={item.route}
            />
          )
        }

        return (
          <SubMenuItemWithClickHandler
            key={item.translatedTextKey}
            icon={item.icon}
            translatedTextKey={item.translatedTextKey}
            onClick={item.onClick}
            isActive={item.isActive}
          />
        )
      })}
    </Stack>
  )
}
